import { useEffect, useRef, useState } from 'react';
import {
    recuperaImagenBitXEntidad,
    recuperaImagenByLista
} from '../../services/empleado/EmpleadoDotacionService';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { dateFromDateBack, dateStringF1, formatToJava } from '../../util/dateUtils';
import FotoMiniatura from './FotoMiniatura';
import { Button } from 'primereact/button';
import {
    eliminarDocumento,
    guardarDocumentoNomina,
    visualizarDocumento
} from '../../services/empleado/empleadoDocumentoService';
import { Toast } from 'primereact/toast';
import { confirmDialog } from 'primereact/confirmdialog';

const DotacionImagenes = ({ datosDotacion, tipoRegistro }) => {

    const [lstImagenes, setLstImagenes] = useState([]);
    const [reemplazandoId, setReemplazandoId] = useState(null);
    const [refresca, setRefresca] = useState(false);

    const miRef = useRef(null);
    const fileInputRef = useRef(null);
    const rowReemplazoRef = useRef(null);

    useEffect(() => {
        const recuperaCabecera = async () => {
            try {
                const data = await recuperaImagenBitXEntidad(
                    datosDotacion.empleadoId,
                    tipoRegistro.configuracionDetId,
                    datosDotacion.dotacionId
                );
                const listaCab = data.map((item) => item.documentoCabId);
                const imagen = await recuperaImagenByLista(listaCab);
                const imageTotal = imagen.map((item) => {
                    return {
                        ...item,
                        fechaLit: dateStringF1(dateFromDateBack(item.fechaCrea)),
                        fechaEmiLit: dateStringF1(dateFromDateBack(item.fechaEmision))
                    };
                });
                //console.log ('HHHH88= ', imageTotal)
                setLstImagenes(imageTotal);
            } catch (err) {
                console.error('ERROR: ', err.message);
            }
        };
        recuperaCabecera();
    }, [datosDotacion.dotacionId, datosDotacion.empleadoId, tipoRegistro.configuracionDetId, refresca]);

    const handleDescargarDocumento = async (rowData) => {
        try {
            // 1. Obtenemos el archivo (Blob)
            const blob = await visualizarDocumento(rowData.id);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            // 2. Determinamos la extensión
            let extension = '';

            // A) Intentamos sacarla de la URL original si existe (ej: "foto.jpg")
            if (rowData.archivoUrl) {
                const partes = rowData.archivoUrl.split('.');
                if (partes.length > 1) {
                    extension = partes.pop().toLowerCase();
                    // Limpieza básica por si la URL tiene parámetros extra
                    if (extension.length > 4) extension = '';
                }
            }

            // B) Si no hay extensión en la URL, la sacamos del tipo de archivo (MIME Type)
            if (!extension) {
                if (blob.type === 'application/pdf') extension = 'pdf';
                else if (blob.type === 'image/jpeg') extension = 'jpg';
                else if (blob.type === 'image/png') extension = 'png';
                else if (blob.type.includes('word')) extension = 'docx';
                else if (blob.type.includes('sheet') || blob.type.includes('excel'))
                    extension = 'xlsx';
                else extension = 'dat'; // Binario genérico
            }

            // 3. Armamos el nombre final
            // Usamos 'nombreArchivo' si viene del backend, o generamos uno genérico con la extensión detectada
            let nombreFinal = rowData.nombreArchivo || `documento_${rowData.id}`;

            // Si el nombre no tiene punto, le pegamos la extensión que encontramos
            if (nombreFinal.indexOf('.') === -1 && extension) {
                nombreFinal = `${nombreFinal}.${extension}`;
            }

            link.setAttribute('download', nombreFinal);

            // 4. Descarga
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error al descargar:', error);
            // Puedes usar toast si lo prefieres: toast.current.show(...)
            alert('No se pudo descargar el archivo.');
        }
    };

    const deleteFoto = (rowData) => {
        confirmDialog({
            message: `¿Eliminar la imagen "${rowData.descripcion || 'sin descripcion'}"?`,
            header: 'Confirmar eliminacion',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Si, eliminar',
            rejectLabel: 'Cancelar',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    await eliminarDocumento(rowData.id);
                    miRef.current.show({
                        severity: 'success',
                        summary: 'Eliminado',
                        detail: 'Imagen eliminada.'
                    });
                    // Actualizacion optimista + refresco de servidor.
                    setLstImagenes(prev => prev.filter(img => img.id !== rowData.id));
                    setRefresca(prev => !prev);
                } catch (error) {
                    console.error('Error borrando foto:', error);
                    miRef.current.show({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'No se pudo borrar.'
                    });
                }
            }
        });
    };

    // Dispara el file picker para reemplazar la imagen de la fila.
    const iniciarReemplazo = (rowData) => {
        rowReemplazoRef.current = rowData;
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
            fileInputRef.current.click();
        }
    };

    const onArchivoReemplazoSeleccionado = (e) => {
        const archivo = e.target.files && e.target.files[0];
        if (!archivo) return;
        const row = rowReemplazoRef.current;
        if (!row) return;

        confirmDialog({
            message: `¿Reemplazar la imagen actual por "${archivo.name}"?`,
            header: 'Confirmar reemplazo',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Si, reemplazar',
            rejectLabel: 'Cancelar',
            acceptClassName: 'p-button-warning',
            accept: async () => {
                setReemplazandoId(row.id);
                try {
                    // Reemplazo atomico: el backend (/documentos/editar/{id}) borra el archivo
                    // anterior en la nube, sube el nuevo y actualiza el mismo registro de
                    // Documento. Mantiene la cabecera original, sin orfanos ni duplicados.
                    await guardarDocumentoNomina(
                        {
                            id: row.id,
                            entidadId: datosDotacion.empleadoId,
                            codigoTipoDocumento: tipoRegistro.codigo,
                            categoriaId: datosDotacion.tipoDotacionConfId,
                            descripcion: row.descripcion,
                            referenciaDetalle: 'DOTACION_ID ' + datosDotacion.dotacionId,
                            fechaEmision: row.fechaEmision
                                ? formatToJava(dateFromDateBack(row.fechaEmision))
                                : null
                        },
                        archivo
                    );
                    miRef.current.show({
                        severity: 'success',
                        summary: 'Reemplazado',
                        detail: 'Imagen reemplazada correctamente.'
                    });
                    setRefresca(prev => !prev);
                } catch (error) {
                    console.error('Error reemplazando foto:', error);
                    miRef.current.show({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'No se pudo reemplazar la imagen.'
                    });
                } finally {
                    setReemplazandoId(null);
                    rowReemplazoRef.current = null;
                }
            },
            reject: () => {
                rowReemplazoRef.current = null;
            }
        });
    };

    const archivoBodyTemplate = (rowData) => {
        if (!rowData.id) return null;

        return (
            <div className="flex gap-2 justify-content-center">
                <Button
                    icon="pi pi-download"
                    className="p-button-rounded p-button-text p-button-success"
                    tooltip="Descargar"
                    tooltipOptions={{ position: 'top' }}
                    onClick={() => handleDescargarDocumento(rowData)}
                />
                <Button
                    icon="pi pi-refresh"
                    className="p-button-rounded p-button-text p-button-warning"
                    tooltip="Reemplazar"
                    tooltipOptions={{ position: 'top' }}
                    loading={reemplazandoId === rowData.id}
                    onClick={() => iniciarReemplazo(rowData)}
                />
                <Button
                    icon="pi pi-trash"
                    className="p-button-rounded p-button-text p-button-danger"
                    onClick={() => deleteFoto(rowData)}
                    tooltip="Eliminar Foto"
                    tooltipOptions={{ position: 'top' }}
                />
            </div>
        );
    };

    return (
        <>
            <Toast ref={miRef} />
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*,application/pdf"
                style={{ display: 'none' }}
                onChange={onArchivoReemplazoSeleccionado}
            />
            <div className="formgrid grid p-fluid mb-3">
                <div className="field col-12 md:col-4">
                    <span className="font-black">Grupo Dotacion: </span>
                </div>
                <div className="field col-12 md:col-4">
                    <span>Item Dotacion: </span>
                </div>
                <div className="field col-12 md:col-4">
                    <span>cantidad: </span>
                </div>
                <div className="field col-12 md:col-4">
                    <span className="inline-block w-full bg-gray-100 border border-gray-300 text-gray-600 p-2 rounded-md text-base shadow-inner">
                        {datosDotacion.grupoDotacionNombre}
                    </span>
                </div>
                <div className="field col-12 md:col-4">
                    <span className="inline-block w-full bg-gray-100 border border-gray-300 text-gray-600 p-2 rounded-md text-base shadow-inner">
                        {datosDotacion.tipoDotacionNombre}
                    </span>
                </div>
                <div className="field col-12 md:col-4">
                    <span className="inline-block w-full bg-gray-100 border border-gray-300 text-gray-600 p-2 rounded-md text-base shadow-inner">
                        {datosDotacion.cantidad}
                    </span>
                </div>
                <div className="field col-12 md:col-4">
                    <span>Comentario: </span>
                </div>
                <div className="field col-12 md:col-8">
                    
                </div>
                <div className="field col-12 md:col-12">
                    <span className="inline-block w-full bg-gray-100 border border-gray-300 text-gray-600 p-2 rounded-md text-base shadow-inner">
                        {datosDotacion.comentario}
                    </span>
                </div>
                {/* DATATABLE */}
                <div className="field col-12 md:col-12">
                    <DataTable value={lstImagenes} emptyMessage='No hay imagenes disponibles'>
                        <Column
                            header="Miniatura"
                            body={(rowData) => (
                                <FotoMiniatura
                                    key={`${rowData.id}-${rowData.codigoUnico || rowData.fechaCrea || ''}`}
                                    id={rowData.id}
                                    descripcion={rowData.descripcion}
                                />
                            )}
                            style={{ width: '80px', textAlign: 'center' }}
                        ></Column>
                        <Column header="descripcion" field="descripcion"></Column>
                        <Column header="Fecha" field="fechaEmiLit"></Column>
                        <Column
                            header="Archivo"
                            body={archivoBodyTemplate}
                            style={{ width: '70px', textAlign: 'center' }}
                        ></Column>
                    </DataTable>
                </div>
            </div>
        </>
    );
};

export default DotacionImagenes;
